import { ArrowUturnLeftIcon } from "@heroicons/react/16/solid";
import { TrashIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import ButtonWithPopoverDots from "components/buttons/ButtonWithPopoverDots";
import { t } from "i18next";
import type { FunctionComponent } from "react";
import { useCallback, useMemo, useState } from "react";

import BreadcrumbsWrapper from "~/components/BreadcrumbsV2";
import BreadcrumbElement from "~/components/BreadcrumbsV2/components/BreadcrumbElement";
import Button from "~/components/buttons/Button";
import ButtonNewItem from "~/components/buttons/ButtonNewItem";
import type { PopoverItem } from "~/components/buttons/ButtonWithPopoverDots/ButtonWithPopoverDots.tsx";
import ContentWrapper from "~/components/ContentWrapper";
import Headline from "~/components/Headline";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import { useAuth } from "~/contexts/AuthContext";
import type { InvoiceCommentWithDate } from "~/modules/billing/api/invoiceComment/invoiceCommentTypes.ts";
import { invoiceStatusId } from "~/modules/billing/api/invoiceStatus/invoiceStatusId.ts";
import type { InvoiceStatus } from "~/modules/billing/api/invoiceStatus/invoiceStatusIdTypes.ts";
import { getInvoiceTypeDisplayNameById, invoiceTypeId } from "~/modules/billing/api/invoiceType.ts";
import {
	advanceInvoiceStatus,
	revertInvoiceStatus,
} from "~/modules/billing/api/processedInvoice/processedInvoiceApiDispatchers.ts";
import {
	ALL_PROCESSED_INVOICES_QUERY_KEY,
	INVOICE_QUERY_KEY, PROJECTS_INVOICES_QUERY_KEY,
} from "~/modules/billing/api/processedInvoice/processedInvoiceQueries.ts";
import type { Invoice, InvoiceStatusId } from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import AdvanceInvoiceDetailsSection
	from "~/modules/billing/components/InvoiceDetailsView/components/AdvanceInvoiceDetailsSection.tsx";
import CreateInvoiceCommentSidebar
	from "~/modules/billing/components/InvoiceDetailsView/components/CreateInvoiceCommentSidebar/CreateInvoiceCommentSidebar.tsx";
import CreateReversalInvoiceSidebar
	from "~/modules/billing/components/InvoiceDetailsView/components/CreateReversalInvoiceSidebar/CreateReversalInvoiceSidebar.tsx";
import CurrentInvoiceToDos from "~/modules/billing/components/InvoiceDetailsView/components/CurrentInvoiceToDos.tsx";
import DeleteInvoiceCommentModal
	from "~/modules/billing/components/InvoiceDetailsView/components/DeleteInvoiceCommentModal";
import DeleteInvoiceSidebar
	from "~/modules/billing/components/InvoiceDetailsView/components/DeleteInvoiceSidebar/DeleteInvoiceSidebar.tsx";
import InvoiceComment from "~/modules/billing/components/InvoiceDetailsView/components/InvoiceComment";
import InvoiceDeliveryPeriodSection
	from "~/modules/billing/components/InvoiceDetailsView/components/InvoiceDeliveryPeriodSection.tsx";
import InvoiceDescriptionSection
	from "~/modules/billing/components/InvoiceDetailsView/components/InvoiceDescriptionSection.tsx";
import InvoiceDetailsSection from "~/modules/billing/components/InvoiceDetailsView/components/InvoiceDetailsSection";
import InvoiceNumberHeader from "~/modules/billing/components/InvoiceDetailsView/components/InvoiceNumberHeader";
import InvoicePaymentSection
	from "~/modules/billing/components/InvoiceDetailsView/components/InvoicePaymentSection.tsx";
import InvoiceRecipientSection
	from "~/modules/billing/components/InvoiceDetailsView/components/InvoiceRecipientSection.tsx";
import InvoiceStatusProgressBar
	from "~/modules/billing/components/InvoiceDetailsView/components/InvoiceStatusProgressBar/InvoiceStatusProgressBar.tsx";
import InvoiceTotalsSection from "~/modules/billing/components/InvoiceDetailsView/components/InvoiceTotalsSection.tsx";
import ReversalInvoiceCard from "~/modules/billing/components/InvoiceDetailsView/components/ReversalInvoiceCard";
import ReversedInvoiceCard from "~/modules/billing/components/InvoiceDetailsView/components/ReversedInvoiceCard";
import RevertInvoiceStatusModal
	from "~/modules/billing/components/InvoiceDetailsView/components/RevertInvoiceStatusModal/RevertInvoiceStatusModal.tsx";
import UpdateInvoiceCommentSidebar
	from "~/modules/billing/components/InvoiceDetailsView/components/UpdateInvoiceCommentSidebar/UpdateInvoiceCommentSidebar.tsx";
import UpdateInvoiceDeliveryPeriodSidebar
	from "~/modules/billing/components/InvoiceDetailsView/components/UpdateInvoiceDeliveryPeriodSidebar/UpdateInvoiceDeliveryPeriodSidebar.tsx";
import UpdateInvoicePaymentInformationSidebar
	from "~/modules/billing/components/InvoiceDetailsView/components/UpdateInvoicePaymentInformationSidebar/UpdateInvoicePaymentInformationSidebar.tsx";
import UpdateInvoiceRecipientSidebar
	from "~/modules/billing/components/InvoiceDetailsView/components/UpdateInvoiceRecipientSidebar/UpdateInvoiceRecipientSidebar.tsx";
import UpdateInvoiceTotalSidebar
	from "~/modules/billing/components/InvoiceDetailsView/components/UpdateInvoiceTotalSidebar/UpdateInvoiceTotalSidebar.tsx";
import UpdateNonProjectInvoiceDescriptionSidebar
	from "~/modules/billing/components/InvoiceDetailsView/components/UpdateNonProjectInvoiceDescriptionSidebar/UpdateNonProjectInvoiceDescriptionSidebar.tsx";
import {
	getInvoiceDetailsBackPath,
	type ValidInvoiceDetailsNavigatedFromOrigin,
	validInvoiceDetailsNavigatedFromOrigins,
} from "~/modules/billing/components/InvoiceDetailsView/invoiceDetailsUtils.ts";
import AssignProcessedInvoiceToOrderSidebar
	from "~/modules/billing/components/InvoiceDetailsView/tempComponents/AssignProcessedInvoiceToOrderSidebar";
import ExcelImportErrors from "~/modules/billing/components/InvoiceDetailsView/tempComponents/ExcelImportErrors";
import { wipStatuses } from "~/modules/billing/utils/billingUtils.ts";
import type { QuotaWithRelations } from "~/modules/client/api/quota/quotaTypes.ts";
import { PermissionNames } from "~/types/entityNames.ts";
import { formatNumberToCurrency } from "~/utils/currencyUtils.ts";
import { formatSmartDateRange } from "~/utils/dateAndTimeUtils.ts";
import { byObjectProperty } from "~/utils/sortFunctions.ts";

type Props = {
	invoiceData: Invoice;
	invoiceStatuses: InvoiceStatus[];
	navigatedFrom?: ValidInvoiceDetailsNavigatedFromOrigin;
	quota?: QuotaWithRelations
};

const sentAndPaidStatusIds = [invoiceStatusId.SentToClient, invoiceStatusId.Paid] as InvoiceStatusId[];

const InvoiceDetailsView: FunctionComponent<Props> = ({ invoiceData, invoiceStatuses, navigatedFrom, quota }) => {
	const queryClient = useQueryClient();
	const { hasAnyPermission } = useAuth();
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [serverErrorMessage, setServerErrorMessage] = useState<string>("");
	const userCanManageInvoices = hasAnyPermission([PermissionNames.CanManageInvoices]);

	const [invoiceCommentDataToUpdate, setInvoiceCommentDataToUpdate] = useState<InvoiceCommentWithDate | undefined>(undefined);
	const [invoiceCommentDataToDelete, setInvoiceCommentDataToDelete] = useState<InvoiceCommentWithDate | undefined>(undefined);
	const [statusIdToRevertTo, setStatusIdToRevertTo] = useState<InvoiceStatusId | undefined>(undefined);
	const [showAssignToOrderSidebar, setShowAssignToOrderSidebar] = useState<boolean>(false);
	const [showCreateInvoiceCommentSidebar, setShowCreateInvoiceCommentSidebar] = useState<boolean>(false);
	const [showCreateReversalInvoiceSidebar, setShowCreateReversalInvoiceSidebar] = useState<boolean>(false);
	const [showDeleteInvoiceSidebar, setShowDeleteInvoiceSidebar] = useState<boolean>(false);
	const [showUpdateDeliveryPeriodSidebar, setShowUpdateDeliveryPeriodSidebar] = useState<boolean>(false);
	const [showUpdateDescriptionSidebar, setShowUpdateDescriptionSidebar] = useState<boolean>(false);
	const [showUpdateInvoicePaymentInformationSidebar, setShowUpdateInvoicePaymentInformationSidebar] = useState<boolean>(false);
	const [showUpdateInvoiceTotalSidebar, setShowUpdateInvoiceTotalSidebar] = useState<boolean>(false);
	const [showUpdateRecipientInformationSidebar, setShowUpdateRecipientInformationSidebar] = useState<boolean>(false);

	const invoiceNumber = invoiceData.invoiceNumber ? invoiceData.invoiceNumber : "Rechnungsnummer tba";
	const isProjectInvoice = invoiceData.isProjectInvoice;
	const isReversalInvoice = invoiceData.invoiceTypeId === invoiceTypeId.ReversalInvoice;
	const isReversedInvoice = invoiceData.invoiceStatusId === invoiceStatusId.Reversed;
	const isAdvanceInvoice = invoiceData.invoiceTypeId === invoiceTypeId.AdvanceInvoice;

	const commentsSorted = useMemo(() => {
		if (invoiceData.comments) {
			return invoiceData.comments.map(comment => {
				return {
					...comment,
					createdAt: new Date(comment.createdAt),
				};
			}).sort(byObjectProperty("createdAt", "desc"));
		}
		return [];
	}, [invoiceData.comments]);

	const Breadcrumbs = useMemo(() => {
		const path = getInvoiceDetailsBackPath(navigatedFrom, invoiceData.projectId || undefined);

		if (navigatedFrom === validInvoiceDetailsNavigatedFromOrigins.PROJECT_INVOICES) {
			return <>
				<BreadcrumbElement text={invoiceData.projectTitle!}
								   url={path} />
				<BreadcrumbElement text="Rechnungen" />
			</>;
		} else {
			return <BreadcrumbElement text="Rechnungen"
									  url={path} />;
		}
	}, [invoiceData.projectId, invoiceData.projectTitle, navigatedFrom]);


	const popoverItems = useMemo(() => {
		const items: PopoverItem[] = [];
		if (!isReversalInvoice && !isAdvanceInvoice && sentAndPaidStatusIds.includes(invoiceData.invoiceStatusId)) {
			items.push({
				label: "Rechnung stornieren",
				onClick: () => setShowCreateReversalInvoiceSidebar(true),
				icon: ArrowUturnLeftIcon,
				iconColorClassName: "fill-danger-500",
			});
		}

		items.push({
			label: "Rechnung löschen",
			onClick: () => setShowDeleteInvoiceSidebar(true),
			icon: TrashIcon,
			iconColorClassName: "text-danger-500",
		});


		return items;
	}, [invoiceData.invoiceStatusId, isAdvanceInvoice, isReversalInvoice]);

	const handleStatusClick = useCallback(async (statusId: InvoiceStatusId, action: "advance" | "revert") => {
		if (!userCanManageInvoices) return;
		try {

			if (action === "advance") {
				setIsBusy(true);
				await advanceInvoiceStatus(invoiceData.id, statusId);
			} else {
				if (wipStatuses.includes(statusId) && wipStatuses.includes(invoiceData.invoiceStatusId)) {
					setIsBusy(true);
					await revertInvoiceStatus(invoiceData.id, statusId);
				} else {
					setStatusIdToRevertTo(statusId);
				}
			}
			await queryClient.refetchQueries({ queryKey: INVOICE_QUERY_KEY(invoiceData.id) });
			queryClient.invalidateQueries({ queryKey: ALL_PROCESSED_INVOICES_QUERY_KEY });

			if (invoiceData.projectId) {
				queryClient.invalidateQueries({ queryKey: PROJECTS_INVOICES_QUERY_KEY(invoiceData.projectId) });
			}
		} catch (error) {
			console.log(error);
			setServerErrorMessage(t("form:unexpectedError"));
		} finally {
			setIsBusy(false);
		}

	}, [invoiceData.id, invoiceData.invoiceStatusId, invoiceData.projectId, queryClient, userCanManageInvoices]);

	const excelErrors = invoiceData.excelRow?.errors ? invoiceData.excelRow?.errors : [];
	const hasMissingOrderId = invoiceData.isProjectInvoice && invoiceData.orders.length === 0;
	const showTodoSection = invoiceData.invoiceStatusId !== invoiceStatusId.Paid && !isReversedInvoice;

	return <div className="auto-grid-2-rows relative grid h-screen">
		{serverErrorMessage && <SidebarErrorOverlay title="Update fehlgeschlafen" onDismiss={()=>setServerErrorMessage("")}>{serverErrorMessage}</SidebarErrorOverlay>}
		{isBusy && <SidebarBusyOverlay />}
		<div className="flex w-full flex-col bg-white">
			<ContentWrapper className="w-full pb-4">
				<BreadcrumbsWrapper>
					{Breadcrumbs}
					<BreadcrumbElement text={invoiceNumber} />
				</BreadcrumbsWrapper>
				<div className="flex w-full items-center gap-x-3 pt-2">
					<div className="flex w-full flex-col">
						<div className="flex w-full items-center justify-between gap-x-2">
							<InvoiceNumberHeader>{invoiceNumber}</InvoiceNumberHeader>
							<div className="flex shrink-0 items-center">
								<Headline type="h2"
										  className={invoiceData.totalAmountCents < 0 ? "text-secondary-500" : ""}>{formatNumberToCurrency(invoiceData.totalAmountCents) + " " + invoiceData.currencySymbol}</Headline>

							</div>
						</div>
						<div className="flex w-full items-center justify-between">
							<Headline type="h5"
									  className="flex items-center gap-x-1">{getInvoiceTypeDisplayNameById(invoiceData.invoiceTypeId)}
								{!isProjectInvoice &&
									<div className="font-normal text-gray-400">(nicht projektbezogen)</div>}
							</Headline>
							<div className="flex items-center">
								<Headline type="h5"
										  className="flex items-center gap-x-1">
									{formatSmartDateRange(new Date(invoiceData.deliveryStartDate), new Date(invoiceData.deliveryEndDate), {
										month: "MMMM",
										year: " yyyy",
									})}</Headline>
							</div>
						</div>
					</div>
					{userCanManageInvoices && popoverItems && <div className="self-start pt-2"><ButtonWithPopoverDots
						items={popoverItems}
						theme="dark"
					/></div>}
				</div>
				<div className="mt-4">
					<InvoiceStatusProgressBar statuses={invoiceStatuses}
											  onStatusClick={userCanManageInvoices ? handleStatusClick : undefined}
											  isSelfBilledInvoice={invoiceData.invoiceTypeId === invoiceTypeId.SelfBilledInvoice}
											  recipientIsSet={!!invoiceData.recipientInformation}
											  currentStatusId={invoiceData.invoiceStatusId} />
				</div>
			</ContentWrapper>
		</div>
		<ContentWrapper className="grid w-full grid-cols-5 content-start gap-4 overflow-y-auto pb-10 pt-4">
			{showTodoSection && <div className="col-span-5">
				<CurrentInvoiceToDos invoiceData={invoiceData}
									 onSetInvoiceRecipientClick={() => setShowUpdateRecipientInformationSidebar(true)}
									 setIsBusy={setIsBusy}
									 setServerErrorMessage={setServerErrorMessage} />
			</div>}
			{excelErrors.length > 0 && <ExcelImportErrors excelErrors={excelErrors}
														  rowNumber={invoiceData.excelRowNumber} />}
			<div className="col-span-3 space-y-4">
				{!quota && isProjectInvoice && <InvoiceDetailsSection
					invoiceData={invoiceData}
					onUpdateClick={hasMissingOrderId && userCanManageInvoices ? () => setShowAssignToOrderSidebar(true) : undefined} />}

				{!isProjectInvoice && <InvoiceDescriptionSection invoiceData={invoiceData}
																 onUpdateClick={() => setShowUpdateDescriptionSidebar(true)} />}

				{quota && <AdvanceInvoiceDetailsSection quota={quota} />}

				<div className="flex flex-col gap-y-4">
					<div className="flex items-center justify-between pl-4">
						<Headline type="h4">Kommentare / Bemerkungen</Headline>
						{commentsSorted.length > 0 && <Button theme="none"
															  size="xs"
															  onClick={() => setShowCreateInvoiceCommentSidebar(true)}>
							Neuer Kommentar</Button>}
					</div>
					{commentsSorted.length > 0 && commentsSorted.map((comment) => {
						return <InvoiceComment key={comment.id}
											   onUpdateClick={() => setInvoiceCommentDataToUpdate(comment)}
											   onDeleteClick={() => setInvoiceCommentDataToDelete(comment)}
											   commentData={comment} />;
					})}
					{commentsSorted.length === 0 && <ButtonNewItem theme="inline"
																   size="sm"
																   onClick={() => setShowCreateInvoiceCommentSidebar(true)}>
						Neuer Kommentar</ButtonNewItem>}
				</div>
			</div>
			<div className="col-span-2 space-y-4">
				<InvoiceDeliveryPeriodSection invoiceData={invoiceData}
											  onUpdateClick={() => setShowUpdateDeliveryPeriodSidebar(true)} />
				<InvoicePaymentSection invoiceData={invoiceData}
									   onUpdateClick={() => setShowUpdateInvoicePaymentInformationSidebar(true)} />
				<InvoiceRecipientSection invoiceData={invoiceData}
										 onUpdateClick={() => setShowUpdateRecipientInformationSidebar(true)} />
				{isReversalInvoice && <ReversedInvoiceCard reversedInvoiceId={invoiceData.reversedInvoiceId!} />}
				{isReversedInvoice && <ReversalInvoiceCard reversedInvoiceId={invoiceData.id} />}

				<InvoiceTotalsSection currencySymbol={invoiceData.currencySymbol}
									  onEditClick={userCanManageInvoices ? () => setShowUpdateInvoiceTotalSidebar(true) : undefined}
									  subtotalCents={invoiceData.subtotalCents}
									  totalAmountCents={invoiceData.totalAmountCents}
									  travelCostCentsBeforeVat={invoiceData.travelCostCentsBeforeVat}
									  vatPercentage={invoiceData.vatPercentage}
									  vatTotalCents={invoiceData.vatTotalCents} />
			</div>
		</ContentWrapper>

		<CreateInvoiceCommentSidebar invoiceId={invoiceData.id}
									 isOpen={showCreateInvoiceCommentSidebar}
									 onClose={() => setShowCreateInvoiceCommentSidebar(false)}
									 projectId={invoiceData.projectId} />
		<UpdateInvoiceCommentSidebar invoiceId={invoiceData.id}
									 isOpen={!!invoiceCommentDataToUpdate}
									 onClose={() => setInvoiceCommentDataToUpdate(undefined)}
									 projectId={invoiceData.projectId}
									 comment={invoiceCommentDataToUpdate} />

		<DeleteInvoiceCommentModal invoiceId={invoiceData.id}
								   isOpen={!!invoiceCommentDataToDelete}
								   onClose={() => setInvoiceCommentDataToDelete(undefined)}
								   projectId={invoiceData.projectId}
								   comment={invoiceCommentDataToDelete} />

		<UpdateInvoiceDeliveryPeriodSidebar isOpen={showUpdateDeliveryPeriodSidebar}
											onClose={() => setShowUpdateDeliveryPeriodSidebar(false)}
											invoiceData={invoiceData} />
		<UpdateNonProjectInvoiceDescriptionSidebar invoiceData={invoiceData}
												   isOpen={showUpdateDescriptionSidebar}
												   onClose={() => setShowUpdateDescriptionSidebar(false)} />

		<UpdateInvoicePaymentInformationSidebar invoiceData={invoiceData}
												isOpen={showUpdateInvoicePaymentInformationSidebar}
												onClose={() => setShowUpdateInvoicePaymentInformationSidebar(false)} />

		<CreateReversalInvoiceSidebar isOpen={showCreateReversalInvoiceSidebar}
									  onClose={() => setShowCreateReversalInvoiceSidebar(false)}
									  originalInvoiceData={invoiceData} />
		<UpdateInvoiceTotalSidebar invoiceData={invoiceData}
								   isOpen={showUpdateInvoiceTotalSidebar}
								   onClose={() => setShowUpdateInvoiceTotalSidebar(false)} />

		<RevertInvoiceStatusModal invoiceData={invoiceData}
								  isOpen={!!statusIdToRevertTo}
								  newStatusId={statusIdToRevertTo}
								  onClose={() => setStatusIdToRevertTo(undefined)} />

		<UpdateInvoiceRecipientSidebar
			invoiceData={invoiceData}
			isOpen={showUpdateRecipientInformationSidebar}
			onClose={() => setShowUpdateRecipientInformationSidebar(false)}
		/>

		{hasMissingOrderId && <AssignProcessedInvoiceToOrderSidebar isOpen={showAssignToOrderSidebar}
																	onClose={() => setShowAssignToOrderSidebar(false)}
																	invoiceData={invoiceData} />}
		<DeleteInvoiceSidebar invoiceData={invoiceData}
							  isOpen={showDeleteInvoiceSidebar}
							  redirectToInvoicesTab={navigatedFrom}
							  onClose={() => setShowDeleteInvoiceSidebar(false)} />
	</div>;
};

export default InvoiceDetailsView;